import FacebookShareButton from '@/components/buttons/FacebookShareButton';
import UpgradeAccountButton from '@/components/buttons/UpgradeAccountButton';
import { QuickLoginModalHOC } from '@/components/modal/LoginModal2HOC';
import { LIBRARY_COLLECTION_DETAIL } from '@/constant/QueryKeyName';
import { copyToClipboard } from '@/helpers/copyToClipboard';
import { recursiveRetrieveCategories } from '@/helpers/doccenLibHelpers';
import useToast from '@/hooks/useToast';
import useToggle from '@/hooks/useToggle';
import { hasUserSubscriptedIdealPlan } from 'common/subscription/checkSubscriptionResource';
import { Button } from 'design-system/components/Button';
import { Divider } from 'design-system/components/Divider';
import Icon from 'design-system/components/Icon/Icon';
import { Typography } from 'design-system/components/Typography';
import dynamic from 'next/dynamic';
import { useContext, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { CollectionDetailContext } from 'state/collection/CollectionDetailProvider';

const DynamicModalCollection = dynamic(
  () =>
    import(
      '@/components/elements/doccen-lib/collection-detail/modal/ModalCollection'
    ),
  {
    ssr: false,
  }
);

const CollectionDetailMainSection = () => {
  const { collection, latestSubscription } = useContext(
    CollectionDetailContext
  );

  const isOwner = collection?.isOwner;
  const urlShare = typeof window !== 'undefined' ? window.location.href : '';

  const toast = useToast();
  const hasUserSubscriptedIdealPlanValue =
    hasUserSubscriptedIdealPlan(latestSubscription) || collection?.isOwner;

  const parsedInitialCategories = useMemo(
    () => recursiveRetrieveCategories(0, {}, collection?.category as any),
    [collection?.category]
  );

  const handleCopyLink = () => {
    copyToClipboard(urlShare, () =>
      toast('Đã sao chép liên kết thành công', 'success')
    );
  };

  const renderBottomContent = () => {
    if (isOwner) {
      return <EditButton />;
    } else {
      return (
        <div className="flex items-center justify-center gap-2 text-Text/LightBackground/Secondary">
          <Typography variant="label">Chia sẻ:</Typography>
          <FacebookShareButton href={urlShare} />
          <Icon
            iconName="link"
            className="cursor-pointer"
            onClick={handleCopyLink}
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex justify-between gap-14">
          <Typography
            variant="body3"
            className="flex-shrink-0 text-Text/LightBackground/Secondary"
          >
            Ngành:
          </Typography>
          <Typography variant="body3" className="text-right">
            {Object.values(parsedInitialCategories)
              .map((item) => item.name)
              .join(' - ')}
          </Typography>
        </div>
        <div className="flex justify-between">
          <Typography
            variant="body3"
            className="text-Text/LightBackground/Secondary"
          >
            Số lượng nội dung:
          </Typography>
          <Typography variant="body3">{collection?.totalItems}</Typography>
        </div>
      </div>
      <Divider />
      <div className="w-full space-y-4">
        {!hasUserSubscriptedIdealPlanValue && (
          <UpgradeAccountButton
            className="w-full"
            ignoreMobile
            disableChangeWhenPackageActive
          >
            Nâng cấp tài khoản
          </UpgradeAccountButton>
        )}
        <QuickLoginModalHOC>
          <Button
            startIcon={{
              name: 'save',
            }}
            className="w-full"
            onClick={() => toast('Tính năng đang phát triển', 'success')}
          >
            Lưu nội dung
          </Button>
        </QuickLoginModalHOC>
      </div>
      <Divider />
      {renderBottomContent()}
    </>
  );
};

export default CollectionDetailMainSection;

function EditButton() {
  const [openModal, toggleModal] = useToggle();
  const { collection } = useContext(CollectionDetailContext);
  const queryClient = useQueryClient();

  return (
    <>
      <Button
        startIcon={{
          name: 'edit',
        }}
        className="w-full"
        onClick={toggleModal}
      >
        Chỉnh sửa
      </Button>

      {openModal && (
        <DynamicModalCollection
          openModal={openModal}
          onClose={toggleModal}
          initialData={collection}
          onMutateSuccess={() =>
            queryClient.refetchQueries([
              LIBRARY_COLLECTION_DETAIL,
              collection?.collectionId,
            ])
          }
        />
      )}
    </>
  );
}
